import React, { DragEventHandler, FunctionComponent, PropsWithChildren, useEffect, useRef, useState } from "react";

import { bemClassNames, doNothing } from "@multiweb/reactUtils";
import { WebcamUtils } from "@multiweb/reactUtils/webcam";
import { IconCode } from "@multiweb/types/contracts";
import { AvatarSnap } from "../AvatarSnap";
import { ButtonThemeVariant } from "../ButtonProvider";
import { IconButton } from "../IconButton";
import { Loader, LoaderSize, LoaderTheme } from "../Loader";
import { AvatarSize, LoaderType, Shape } from "./enum";

export type AvatarComponentProps = {
    /** Zda je přepnut do editačního režimu */
    isEditing?: boolean;
    /** Tvar avatara */
    shape?: Shape;
    /** Kompletní cesta k avatarovi */
    image: string | null;
    /** Velikost avatara */
    size?: AvatarSize;
    /** Kliknutí na pořízení snímku z kamery */
    snapBtnClick?: (blob: Blob | null) => void;
    /** Kliknutí na tlačítko nahrání souboru */
    uploadFileClick?: (files: readonly File[]) => void;
    /** Kliknutí na tlačítko smazání avatara */
    deleteImageClick?: () => void;
    /** Kliknutí na tlačítko opakované nahrávání avatara */
    refreshUploadClick?: (files: readonly File[]) => void;
    /** Informace o tom zda probíhá upload a má se zobrazit loader */
    isLoading?: boolean;
    /** Typ zobrazeného loaderu */
    loaderType?: LoaderType;
    /** Informace o tom, zda je ve stavu chyby */
    isError?: boolean;
    /** Pokud probíhá loading, tak zde je uváděn stav v procentech */
    loadingProgress?: number;
    /** Text tlačítka pro změnu avatara */
    changeButtonText?: string;
    /** Text tlačítka pro smazání avatara */
    deleteButtonText?: string;
    /** Text tlačítka pro pořízení snímku z webcamery */
    snapButtonText?: string;
    /** Text tlačítka pro opětovné nahrání */
    refreshButtonText?: string;
    /** Text popisující chybu, která nastala */
    errorText?: string;
    /** Text zobrazen při nahrávání */
    loadingText?: string;
    /** Pokud je avatar prázdný, umožňuje zobrazit iniciály */
    userInitials?: string;
};

const classNames = bemClassNames("avatar-component");

const SubElementImage = ({ children }: PropsWithChildren<unknown>) => <div className={classNames("image")}>{children}</div>;
const SubElementButtons = ({ children }: PropsWithChildren<unknown>) => <div className={classNames("buttons")}>{children}</div>;

export const AvatarComponent: FunctionComponent<AvatarComponentProps> = ({
    isEditing = false,
    shape = Shape.circle,
    image,
    size = AvatarSize.medium,
    snapBtnClick,
    uploadFileClick,
    deleteImageClick,
    refreshUploadClick,
    isLoading = false,
    loaderType = LoaderType.spinner,
    deleteButtonText,
    snapButtonText,
    changeButtonText,
    refreshButtonText,
    userInitials = "",
    loadingProgress = 0,
    isError = false,
    errorText = "",
    loadingText = "",
}) => {
    const [isSnapping, setIsSnapping] = useState(false);
    const [isWebcamAvailable, setIsWebcamAvailable] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isEmpty = !image;

    const selectFileCallback = () => {
        fileInputRef?.current?.click();
    };

    const onChangeCallback = (isRefreshButton: boolean = false) => {
        if (fileInputRef.current) {
            const files = Array.from(fileInputRef.current?.files ?? []);

            isRefreshButton ? refreshUploadClick?.(files) : uploadFileClick?.(files);
        }
    };

    const cancelUploadCallback = () => {
        // @ts-ignore // pokud klikneme na křížek vynulujeme fileedit
        if (fileInputRef?.current) fileInputRef.current.value = null;
        deleteImageClick?.();
    };

    const dropEventCallback: DragEventHandler = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (e.dataTransfer.items) {
            uploadFileClick?.(
                Array.from(e.dataTransfer.items)
                    .filter(item => item.kind === "file" && item.getAsFile() != null)
                    .map(item => item.getAsFile()!),
            );
        } else {
            uploadFileClick?.(Array.from(e.dataTransfer.files));
        }
    };

    const dragStop: DragEventHandler = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const onRefreshCallback = () => onChangeCallback(true);
    const onUploadCallback = () => onChangeCallback(false);

    const startCaptureCallback = () => setIsSnapping(true);
    const stopCaptureCallback = () => setIsSnapping(false);

    const snapBtnCallback = (data: Blob | null) => {
        setIsSnapping(false);
        snapBtnClick?.(data);
    };

    enum ButtonQAVariant {
        captureStart = "capture-start-button",
        captureStop = "capture-stop-button",
        imageSelect = "image-select-button",
        imageDelete = "image-delete-button",
    }

    useEffect(() => {
        WebcamUtils.info().then((info) => {
            setIsWebcamAvailable(info.length !== 0);
        });
    }, []);

    return (
        <div onDrop={dropEventCallback} onDragOver={dragStop} onDragEnter={dragStop} className={classNames({ shape, size, empty: isEmpty, editing: isEditing, loading: isLoading })}>
            {isLoading || isError
                ? (
                    <SubElementImage>
                        <span className={classNames("text")}>{isError ? errorText : loadingText}</span>
                    </SubElementImage>
                ) :
                (<SubElementImage>
                    {isWebcamAvailable && isSnapping && <AvatarSnap canSnap={true} snapButtonVariant={ButtonThemeVariant.avatar} enabled={isSnapping} snapButtonClick={snapBtnCallback} />}
                    {!isSnapping && image && <img src={image} alt="avatar" />}
                    {!isSnapping && isEmpty && <div className={classNames("user-initials")}>{userInitials}</div>}
                </SubElementImage>
                )}
            {isLoading && !isError && (
                <SubElementButtons>
                    {loaderType === LoaderType.progressBar && <div className={classNames("loading")}><div className={classNames("progress-bar")} style={{ width: `${loadingProgress}%` }} /></div>}
                    {loaderType === LoaderType.spinner && <Loader theme={LoaderTheme.colored} size={LoaderSize.medium} />}
                </SubElementButtons>
            )}

            {!isLoading && !isError && isEditing && (
                <SubElementButtons>
                    <input type="file" accept="image/*" onChange={onUploadCallback} ref={fileInputRef} />
                    {isWebcamAvailable && isSnapping && <IconButton variant={ButtonThemeVariant.avatar} iconCode={IconCode.uiCross} onClick={stopCaptureCallback} disabled={isLoading} qa={ButtonQAVariant.captureStop} />}
                    {isWebcamAvailable && !isSnapping && <IconButton variant={ButtonThemeVariant.avatar} iconCode={IconCode.toolCameraFilled} onClick={startCaptureCallback} disabled={isLoading} title={snapButtonText} qa={ButtonQAVariant.captureStart} />}

                    {!isSnapping && <IconButton variant={ButtonThemeVariant.avatar} iconCode={IconCode.docImageFilled} onClick={selectFileCallback ?? doNothing} disabled={isLoading} title={changeButtonText} qa={ButtonQAVariant.imageSelect} />}
                    {image && <IconButton variant={ButtonThemeVariant.avatar} iconCode={IconCode.editorialTrashFilled} onClick={deleteImageClick ?? doNothing} disabled={isLoading} title={deleteButtonText} qa={ButtonQAVariant.imageDelete} />}
                </SubElementButtons>
            )}
            {isError && (
                <SubElementButtons>
                    <IconButton variant={ButtonThemeVariant.avatar} iconCode={IconCode.uiRefreshFilled} onClick={onRefreshCallback} title={refreshButtonText} />
                    <IconButton variant={ButtonThemeVariant.avatar} iconCode={IconCode.uiCrossFilled} onClick={cancelUploadCallback} title={deleteButtonText} />
                </SubElementButtons>
            )}
        </div>
    );
};
